import {
  isHotelAutocompleteQueryReady,
  normalizeHotelAutocompleteQuery,
} from './hotelAutocompleteQuery';
import {
  pickFirstHotelAutocompleteSuggestion,
  toHotelSearchAutoSelect,
} from './hotelAutocompleteResults';
import {
  HOTEL_SEARCH_AUTOCOMPLETE_PATH,
  HOTEL_SEARCH_INTENT_ACTION,
} from '../constants/hotelSearch.constants';

/**
 * Hotel Search Intent Resolver
 *
 * When the user types a destination but hits Search/Enter without selecting from the
 * dropdown, this module calls search-autocomplete and auto-selects the first result
 * so coordinates can be resolved the same way a manual pick would.
 *
 *   AUTO_SELECT  – first autocomplete match → navigate immediately
 *   NO_MATCH     – nothing found
 *   API_ERROR    – network / server error
 */

/**
 * @param {string} query   – raw text the user typed
 * @param {string} apiBase – e.g. "http://localhost:5000/api"
 * @returns {Promise<Object>} resolution object
 */
export async function resolveSearchIntent(query, apiBase) {
  if (!isHotelAutocompleteQueryReady(query)) {
    return { action: HOTEL_SEARCH_INTENT_ACTION.NO_MATCH, message: 'Please enter at least 2 characters.' };
  }

  const trimmedQuery = normalizeHotelAutocompleteQuery(query);
  let result;
  try {
    const res = await fetch(`${apiBase}${HOTEL_SEARCH_AUTOCOMPLETE_PATH}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: trimmedQuery }),
    });
    result = await res.json();
  } catch (err) {
    return { action: HOTEL_SEARCH_INTENT_ACTION.API_ERROR, message: 'Unable to search. Please try again.' };
  }

  if (!result?.success) {
    return { action: HOTEL_SEARCH_INTENT_ACTION.API_ERROR, message: result?.error || 'Search failed.' };
  }

  const first = pickFirstHotelAutocompleteSuggestion(result);
  const autoSelect = toHotelSearchAutoSelect(first);
  if (autoSelect) {
    return autoSelect;
  }

  return {
    action: HOTEL_SEARCH_INTENT_ACTION.NO_MATCH,
    message: `No results found for "${query}".`,
  };
}

/**
 * User-friendly error message from a resolution object.
 */
export function getErrorMessage(resolution) {
  if (!resolution) return 'Something went wrong. Please try again.';
  if (resolution.message) return resolution.message;
  if (resolution.action === HOTEL_SEARCH_INTENT_ACTION.NO_MATCH) return 'No destinations found. Try a different search.';
  if (resolution.action === HOTEL_SEARCH_INTENT_ACTION.API_ERROR) return 'Unable to search right now. Please try again.';
  return 'Please select a destination from the list.';
}
